const QUOTED_SCOPE = "unibox-quoted-body";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Quita html/head/body para inyectar el MIME en un div. */
export function unwrapEmailDocument(html) {
  let out = String(html || "");
  if (!out) return "";

  out = out.replace(/<!DOCTYPE[^>]*>/gi, "");
  out = out.replace(/<\/?html[^>]*>/gi, "");
  out = out.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, (head) => {
    const styles = [];
    const re = /<style\b[^>]*>[\s\S]*?<\/style>/gi;
    let m;
    while ((m = re.exec(head))) styles.push(m[0]);
    return styles.join("\n");
  });
  out = out.replace(/<body\b([^>]*)>/i, (_, attrs) => {
    const styleMatch = String(attrs || "").match(/style\s*=\s*["']([^"']*)["']/i);
    const alignMatch = String(attrs || "").match(/\balign\s*=\s*["']?(\w+)/i);
    const bits = [];
    if (styleMatch?.[1]) bits.push(styleMatch[1]);
    if (alignMatch?.[1]) bits.push(`text-align:${alignMatch[1]}`);
    return bits.length
      ? `<div data-unibox-body style="${bits.join(";")}">`
      : `<div data-unibox-body>`;
  });
  out = out.replace(/<\/body>/i, "</div>");
  return out.trim();
}

function scopeCssSelectors(css, scope) {
  const parts = String(css || "").split(/(@(?:media|supports|keyframes|font-face)[^{]*{)/i);
  let out = "";
  for (let i = 0; i < parts.length; i++) {
    const chunk = parts[i];
    if (/^@(?:keyframes|font-face)/i.test(chunk)) {
      out += chunk;
      continue;
    }
    if (/^@(?:media|supports)/i.test(chunk)) {
      out += chunk;
      continue;
    }
    out += chunk.replace(/(^|})\s*([^{}]+)\s*{/g, (m, brace, selectors) => {
      const trimmed = selectors.trim();
      if (!trimmed || trimmed.startsWith("@")) return m;
      const scoped = trimmed
        .split(",")
        .map((sel) => {
          const s = sel.trim();
          if (!s) return s;
          if (/^(from|to|\d+%)$/i.test(s)) return s;
          if (/^(html|body|:root|\*)$/i.test(s)) return `.${scope}`;
          if (s.includes(`.${scope}`)) return s;
          return `.${scope} ${s}`;
        })
        .join(", ");
      return `${brace} ${scoped} {`;
    });
  }
  return out;
}

/** Evita que <style> del original (text-align:center en body/*) pinte todo el diálogo. */
export function isolateQuotedEmailHtml(html) {
  let out = unwrapEmailDocument(html);
  if (!out) return "";

  out = out.replace(
    /<style\b([^>]*)>([\s\S]*?)<\/style>/gi,
    (_, attrs, css) => `<style${attrs}>${scopeCssSelectors(css, QUOTED_SCOPE)}</style>`
  );

  return `<div class="${QUOTED_SCOPE}" style="text-align:initial;">${out}</div>`;
}

function quotedHeaderHtml({ title, from, date, subject, to, cc }) {
  const lines = [
    `<div style="text-align:left !important;direction:ltr;font-family:inherit;color:inherit;">`,
    `${escapeHtml(title)}<br/>`,
  ];
  if (from) lines.push(`<b>De:</b> ${escapeHtml(from)}<br/>`);
  if (date) lines.push(`<b>Fecha:</b> ${escapeHtml(date)}<br/>`);
  if (subject) lines.push(`<b>Asunto:</b> ${escapeHtml(subject)}<br/>`);
  if (to) lines.push(`<b>Para:</b> ${escapeHtml(to)}<br/>`);
  if (cc) lines.push(`<b>CC:</b> ${escapeHtml(cc)}<br/>`);
  lines.push(`</div>`);
  return lines.join("\n");
}

/** Cabecera a la izquierda + cuerpo original aislado (centrado UPS, etc.). */
export function buildQuotedOriginalHtml({
  html,
  email,
  title = "---------- Mensaje original ----------",
  includeTo = false,
}) {
  const date = email?.date
    ? new Date(email.date).toLocaleString("es-ES")
    : "";
  const header = quotedHeaderHtml({
    title,
    from: email?.from || "",
    date,
    subject: email?.subject || "",
    to: includeTo ? email?.to_email || email?.to || "" : "",
    cc: includeTo ? email?.cc_email || "" : "",
  });
  const body = isolateQuotedEmailHtml(html);
  return `${header}<div style="margin-top:12px;">${body}</div>`;
}

export const quotedOriginalPreviewSx = {
  mt: 2,
  border: "1px solid #e2e8f0",
  borderRadius: 1,
  p: 2,
  bgcolor: "#fafafa",
  maxHeight: 420,
  overflow: "auto",
  fontSize: 14,
  textAlign: "left",
  "& table": { borderCollapse: "collapse", maxWidth: "100%" },
  "& img": { maxWidth: "100%", height: "auto" },
  [`& .${QUOTED_SCOPE}`]: {
    overflow: "auto",
  },
};
